import { CopyOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { FC, useCallback } from "react";
import { createUniqueId } from "tm-shared/utils";

import { BaseModal } from "@/components/BaseModal";
import { NetworkIndicator } from "@/components/NetworkIndicator";
import { PrimaryText } from "@/components/Typography/Styled";
import { TaskProgressDisplay } from "@/components/UI/TaskProgressDisplay";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { useUserPayload } from "@/hooks/useUserPayload";
import { restartRequestQueue } from "@/store/slices/offline";
import { RequestQueueItem } from "@/types/requestQueue";
import { copyObjectToClipboard, mapRequestQueueItemForCopy } from "@/utils";

interface TaskProgressModalProps {
    onClose: () => void;
    queueItem: RequestQueueItem;
}

export const TaskProgressModal: FC<TaskProgressModalProps> = ({
    onClose,
    queueItem,
}) => {
    const { offlineMode } = useAppSelector((s) => s.offline);
    const dispatch = useAppDispatch();

    const { isRunning: isRunningQueue } = useQueueInfo();

    const { progress, commands, id } = queueItem;
    const isRunning = progress?.isRunning;

    const { userPayload } = useUserPayload();

    const handleRetry = useCallback(() => {
        dispatch(restartRequestQueue(id));
    }, [dispatch, id]);

    const handleCopyQueue = useCallback(() => {
        const result = {
            user: userPayload,
            queue: mapRequestQueueItemForCopy(queueItem),
        };

        copyObjectToClipboard(result);
    }, [queueItem, userPayload]);

    const progressDisplayData = progress
        ? progress.tasks
        : commands.map(({ title }) => ({
              id: createUniqueId(),
              label: title,
              status: QueueTaskStatus.Pending,
          }));

    const error = progress?.tasks?.[progress?.lastErrorIndex ?? ""]?.error;

    return (
        <BaseModal
            open
            onClose={onClose}
            title={
                isRunning
                    ? "Завантаження даних на сервер"
                    : "Задача знаходиться в черзі"
            }
            footer={[]}
            actions={null}
            maskClosable
            icon={<NetworkIndicator />}
        >
            {isRunning ? (
                <div>
                    <PrimaryText textAlign="center">
                        Не вимикайте екран
                    </PrimaryText>
                    <PrimaryText textAlign="center">
                        Не закривайте застосунок
                    </PrimaryText>
                    <PrimaryText textAlign="center">
                        Не оновлюйте сторінку
                    </PrimaryText>
                </div>
            ) : (
                <Flex vertical gap={8}>
                    <Flex gap={8} justify="space-between">
                        <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            disabled={offlineMode || isRunningQueue}
                            onClick={handleRetry}
                        >
                            Синхронізувати
                        </Button>

                        <Button
                            shape="circle"
                            icon={<CopyOutlined style={{ fontSize: 22 }} />}
                            onClick={handleCopyQueue}
                            type="text"
                        />
                    </Flex>

                    {error && (
                        <PrimaryText $textColor="error">{error}</PrimaryText>
                    )}
                </Flex>
            )}

            <TaskProgressDisplay tasks={progressDisplayData} />
        </BaseModal>
    );
};
